import { ChevronDown, SearchX, Search, ShoppingCart } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useSearchParams } from "react-router-dom";
import { CartPanel } from "../components/cart/CartPanel";
import { ProductCard, ProductCardSkeleton } from "../components/product/ProductCard";
import { ProductDetailsModal } from "../components/product/ProductDetailsModal";
import { Newsletter } from "../components/sections/Newsletter";
import { BuilderBanner } from "../components/sections/BuilderBanner";
import { PageHeader } from "../components/sections/PageHeader";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Reveal } from "../components/ui/Reveal";
import { SectionHeading } from "../components/ui/SectionHeading";
import { useCart } from "../context/CartContext";
import { useCustomizer } from "../context/CustomizerContext";
import { useFavorites } from "../hooks/useFavorites";
import { useProducts } from "../hooks/useProducts";
import type { CategoryFilter, Product } from "../types";
import { capitalize, cn, formatPrice, normalizeText } from "../utils/format";

const PAGE_SIZE = 12;

const Cardapio = () => {
  const { products: produtos, loading, error } = useProducts();
  const [searchParams] = useSearchParams();
  const [categoria, setCategoria] = useState<CategoryFilter>("todos");
  const [searchTerm, setSearchTerm] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [details, setDetails] = useState<Product | null>(null);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { quantityOf, decrementItem, count, subtotal, openCart } = useCart();
  const { optionsFor, openCustomizer, addOrCustomize } = useCustomizer();

  useEffect(() => {
    if (error) toast.error("Erro ao carregar cardápio.");
  }, [error]);

  const categorias = useMemo<CategoryFilter[]>(
    () => ["todos", ...new Set(produtos.map((p) => p.category))],
    [produtos],
  );

  // Categoria vinda da faixa de categorias da Home (?categoria=...).
  const requested = searchParams.get("categoria");
  useEffect(() => {
    if (!requested) return;
    const match = categorias.find((c) => normalizeText(c) === normalizeText(requested));
    if (match) setCategoria(match);
  }, [requested, categorias]);

  const produtosFiltrados = useMemo(() => {
    const termo = searchTerm.toLowerCase();
    return produtos.filter(
      (p) =>
        (categoria === "todos" || p.category === categoria) &&
        (p.name?.toLowerCase().includes(termo) || p.description?.toLowerCase().includes(termo)),
    );
  }, [produtos, categoria, searchTerm]);

  const shown = produtosFiltrados.slice(0, visible);

  const selectCategory = (cat: CategoryFilter) => {
    setCategoria(cat);
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageHeader title="Cardápio" current="Cardápio" />

      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
        <Reveal>
          <SectionHeading
            align="center"
            eyebrow="Escolha seu favorito"
            title="Nosso"
            highlight="Cardápio"
            description="Navegue pelas categorias e selecione seus burgers clicando no ícone de adicionar."
          />
        </Reveal>

        <div className="mt-10 flex flex-col items-center gap-6">
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-5 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden="true" />
            <label htmlFor="busca" className="sr-only">
              Buscar no cardápio
            </label>
            <input
              id="busca"
              type="search"
              placeholder="Buscar no cardápio..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setVisible(PAGE_SIZE);
              }}
              className="field h-12 rounded-full bg-card pl-12 pr-5"
            />
          </div>

          <div
            role="group"
            aria-label="Filtrar por categoria"
            className="no-scrollbar -mx-4 flex max-w-[calc(100%+2rem)] gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:max-w-full sm:flex-wrap sm:justify-center sm:px-0"
          >
            {categorias.map((cat) => {
              const active = categoria === cat;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => selectCategory(cat)}
                  aria-pressed={active}
                  className={cn(
                    "relative shrink-0 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors",
                    active ? "border-primary text-night" : "border-line text-white/80 hover:border-primary/60 hover:text-white",
                  )}
                >
                  {active && (
                    <motion.span
                      layoutId="filter-pill"
                      className="absolute inset-0 rounded-full bg-primary"
                      transition={{ type: "spring", stiffness: 450, damping: 35 }}
                    />
                  )}
                  <span className="relative">{cat === "todos" ? "Todos" : capitalize(cat)}</span>
                </button>
              );
            })}
          </div>
        </div>

        {(categoria === "todos" || normalizeText(categoria).includes("hamb")) && <BuilderBanner className="mt-10" />}

        <div className="mt-12 xl:grid xl:grid-cols-[minmax(0,1fr)_340px] xl:items-start xl:gap-8">
          <div>
            {loading && produtos.length === 0 ? (
              <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-label="Carregando cardápio">
                {Array.from({ length: 6 }, (_, i) => (
                  <li key={i}>
                    <ProductCardSkeleton />
                  </li>
                ))}
              </ul>
            ) : produtosFiltrados.length === 0 ? (
              <div className="flex flex-col items-center rounded-card border border-dashed border-line px-6 py-20 text-center">
                <SearchX className="size-10 text-primary" aria-hidden="true" />
                <p className="mt-4 font-display text-3xl tracking-wide">Nenhum item encontrado</p>
                <p className="mt-1 text-sm text-muted">
                  {error ? "Não foi possível carregar o cardápio agora." : "Tente outra categoria ou termo de busca."}
                </p>
              </div>
            ) : (
              <>
                <motion.ul layout className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  <AnimatePresence mode="popLayout" initial={false}>
                    {shown.map((p, i) => {
                      const customizable = optionsFor(p.id) !== null;
                      return (
                        <ProductCard
                          key={p.id}
                          product={p}
                          index={i % PAGE_SIZE}
                          quantity={quantityOf(p.id)}
                          favorite={isFavorite(p.id)}
                          customizable={customizable}
                          onAdd={() => addOrCustomize(p)}
                          onDecrement={() => decrementItem(String(p.id))}
                          onToggleFavorite={() => toggleFavorite(p.id)}
                          onDetails={() => (customizable ? openCustomizer(p) : setDetails(p))}
                        />
                      );
                    })}
                  </AnimatePresence>
                </motion.ul>

                <div className="mt-12 flex flex-col items-center gap-3">
                  <p className="text-xs text-muted">
                    Mostrando {shown.length} de {produtosFiltrados.length} itens
                  </p>
                  <AnimatePresence>
                    {visible < produtosFiltrados.length && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.96 }}
                      >
                        <Button
                          size="lg"
                          onClick={() => setVisible((v) => v + PAGE_SIZE)}
                          icon={<ChevronDown className="size-5 animate-bounce motion-reduce:animate-none" aria-hidden="true" />}
                        >
                          Carregar mais
                        </Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            )}
          </div>

          {/* Carrinho fixo ao lado no desktop (como na versão anterior) */}
          <aside className="hidden xl:sticky xl:top-28 xl:block" aria-label="Carrinho">
            <Card className="h-[calc(100dvh-8.5rem)] max-h-[780px] overflow-hidden">
              <CartPanel titleId="cart-sidebar-title" />
            </Card>
          </aside>
        </div>
      </section>

      <Newsletter />

      {/* Atalho para o carrinho no mobile/tablet */}
      <AnimatePresence>
        {count > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed inset-x-4 bottom-4 z-40 sm:left-auto sm:right-6 sm:w-auto xl:hidden"
          >
            <button
              type="button"
              onClick={openCart}
              className="btn-glow flex w-full items-center justify-between gap-6 rounded-full bg-primary py-3 pl-5 pr-3 font-semibold text-night shadow-glow"
            >
              <span className="flex items-center gap-2">
                <ShoppingCart className="size-5" aria-hidden="true" />
                Ver carrinho · {count} {count === 1 ? "item" : "itens"}
              </span>
              <span className="rounded-full bg-night px-4 py-1.5 text-sm text-primary">{formatPrice(subtotal)}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <ProductDetailsModal
        product={details}
        favorite={details ? isFavorite(details.id) : false}
        onToggleFavorite={() => details && toggleFavorite(details.id)}
        onClose={() => setDetails(null)}
      />
    </>
  );
};

export default Cardapio;
